import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import styles from './UpgradePrompt.module.css';

const TIER_LABELS = {
  individual_pro: 'Pro',
  team_pro: 'Pro',
  team_basic: 'Basic',
  teams: 'Teams',
};

/**
 * Names the tiers that would unlock something, without pretending a personal
 * upgrade solves a company-scoped gap.
 */
function unlockSentence(unlockedBy = [], scope = 'user') {
  const labels = [...new Set(unlockedBy.map((t) => TIER_LABELS[t] || t))];
  if (labels.length === 0) return null;
  if (scope === 'company') {
    return `Available on ${labels.join(' or ')}. Ask your company owner or admin to upgrade.`;
  }
  return `Available on ${labels.join(' or ')}.`;
}

/**
 * The gate a free reader sees where paid content would be.
 *
 * `preview` is whatever the server chose to send — for Insights, the headline and
 * a short blurb. The prompt renders what arrived and never implies there is more
 * hidden in the page.
 */
export default function UpgradePrompt({
  feature,
  title,
  body,
  preview = null,
  unlockedBy = null,
  scope = 'user',
  compact = false,
  ctaLabel = 'See Pro plans',
  ctaTo = '/upgrade?focus=pro',
  showTierLine = true,
}) {
  const navigate = useNavigate();
  const { lockInfo } = useEntitlements();

  const info = lockInfo(feature);
  const tiers = unlockedBy || info?.unlockedBy || [];
  const heading = title || info?.label || 'Upgrade required';
  const copy = body || info?.upgradeCopy || null;
  const sentence = unlockSentence(tiers, scope);
  const companyScoped = scope === 'company' || info?.scope === 'company';

  return (
    <section
      className={`${styles.prompt} ${compact ? styles.compact : ''}`}
      aria-label={`${heading} — upgrade required`}
    >
      {preview?.headline ? (
        <div className={styles.preview}>
          <p className={styles.previewLabel}>Latest edition</p>
          <h3 className={styles.previewHeadline}>{preview.headline}</h3>
          {preview.blurb ? <p className={styles.previewBlurb}>{preview.blurb}</p> : null}
          {preview.industryCount > 0 || preview.developmentCount > 0 ? (
            <p className={styles.previewMeta}>
              {[
                preview.industryCount > 0
                  ? `${preview.industryCount} industry ${preview.industryCount === 1 ? 'briefing' : 'briefings'}`
                  : null,
                preview.developmentCount > 0 ? `${preview.developmentCount} developments` : null,
                preview.sourceArticleCount > 0 ? `${preview.sourceArticleCount} sources` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={styles.gate}>
        <span className={styles.lockIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
        </span>
        <h4 className={styles.gateTitle}>{heading}</h4>
        {copy ? <p className={styles.gateBody}>{copy}</p> : null}
        {showTierLine && sentence ? <p className={styles.gateTiers}>{sentence}</p> : null}

        {companyScoped ? null : (
          <button type="button" className={styles.cta} onClick={() => navigate(ctaTo)}>
            {ctaLabel}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Renders children when the feature is granted, and the prompt when it is not.
 * For content the server withholds, prefer reading the server's `locked` flag —
 * this wrapper is for UI that would otherwise render an inert control.
 */
export function Gate({ feature, children, fallback = null, ...promptProps }) {
  const { has, isLoading } = useEntitlements();
  if (isLoading) return null;
  if (has(feature)) return <>{children}</>;
  if (fallback !== null) return fallback;
  return <UpgradePrompt feature={feature} {...promptProps} />;
}
